import type { ReportTemplate, Node, FlowOptions } from "../types/template.js";
import type { Theme } from "../types/theme.js";
import type { DataSource, ReportData, FieldSchema } from "../types/data.js";
import type { BlockRegistry } from "../registry/registry.js";
import { bindingSpecsFor } from "../registry/registry.js";
import type { BindingInfo, BindingSpec, BlockDefinition, RenderMode, ResolveHookContext } from "../registry/block-definition.js";
import { resolveDefaultProps } from "../registry/block-definition.js";
import { Scope, evaluate, evaluateCondition, splitTemplate, type EvalContext } from "../expr/evaluate.js";
import { toDisplayString } from "../expr/format.js";
const display = toDisplayString;
import type { FormatContext } from "../expr/format.js";
import { parseExpression, checkExpressionSyntax } from "../expr/parser.js";
import { collectPaths } from "../expr/ast.js";
import { SchemaScope, createSchemaScope, resolvePathSchema, typeCheckAst } from "../expr/typecheck.js";
import { compileCondition } from "../expr/condition.js";
import { resolveStyle } from "../theme/resolve-style.js";
import { mergeTheme } from "../theme/tokens.js";
import { isRichTextDoc, type RichTextDoc, type InlineNode, type Paragraph } from "../richtext/model.js";
import { synthesizeSampleData, synthesizeValue } from "../data/sample.js";
import { describeExpression } from "./describe.js";
import type { ResolvedDocument, ResolvedFlow, ResolvedNode, ResolvedRegion, ResolvedSegment } from "./resolved.js";

export interface ResolveOptions {
  template: ReportTemplate;
  registry: BlockRegistry;
  theme?: Partial<Theme>;
  dataSources?: DataSource[];
  /** Runtime data (preview / PDF). */
  data?: ReportData;
  /** Sample data used in design mode when `data` is absent. */
  sampleData?: ReportData;
  mode?: RenderMode;
  /** Fixed clock for deterministic output. */
  now?: Date;
  locale?: string;
  currency?: string;
  timeZone?: string;
  /** Iterations shown per repeater in design mode. */
  designIterations?: number;
  /** Hard cap on repeater iterations (safety). */
  maxIterations?: number;
  /** Whether per-block style overrides are honoured. */
  allowStyleOverrides?: boolean;
  /** Design mode: still show nodes whose condition is false (marked as hidden). */
  designShowHidden?: boolean;
}

interface Ctx {
  registry: BlockRegistry;
  theme: Theme;
  template: ReportTemplate;
  mode: RenderMode;
  format: FormatContext;
  warnings: ResolvedDocument["warnings"];
  designIterations: number;
  maxIterations: number;
  allowStyleOverrides: boolean;
  designShowHidden: boolean;
  dataSources: DataSource[];
}

/**
 * Resolves a template into a `ResolvedDocument`: evaluates every binding,
 * expands repeaters, decides conditionals and cascades styles.
 */
export function resolveTemplate(opts: ResolveOptions): ResolvedDocument {
  const mode: RenderMode = opts.mode ?? "preview";
  const theme = mergeTheme(opts.theme);
  const template = opts.template;
  const dataSources = opts.dataSources ?? [];
  const now = opts.now ?? new Date();
  const format: FormatContext = {
    locale: opts.locale ?? template.settings?.locale ?? "en-GB",
    currency: opts.currency ?? template.settings?.currency ?? "GBP",
    timeZone: opts.timeZone ?? template.settings?.timeZone,
    now
  };
  const warnings: ResolvedDocument["warnings"] = [];

  let data: ReportData;
  if (mode === "preview") data = opts.data ?? opts.sampleData ?? {};
  else data = opts.data ?? opts.sampleData ?? synthesizeSampleData(dataSources, { now });
  if (mode === "design") {
    // Fill any sources the sample omitted so the canvas never shows holes.
    const synth = synthesizeSampleData(dataSources, { now });
    data = { ...synth, ...data };
  }

  const globals: Record<string, unknown> = {
    page: { number: 1, total: 1 },
    now,
    theme: { name: theme.name, logo: theme.logo, logoDark: theme.logoDark, colors: theme.colors, typography: theme.typography, meta: theme.meta ?? {} }
  };
  const scope = new Scope(globals).child(data);
  const schemaScope = createSchemaScope(dataSources);

  const ctx: Ctx = {
    registry: opts.registry,
    theme,
    template,
    mode,
    format,
    warnings,
    designIterations: Math.max(1, opts.designIterations ?? 1),
    maxIterations: opts.maxIterations ?? 5000,
    allowStyleOverrides: opts.allowStyleOverrides ?? true,
    designShowHidden: opts.designShowHidden ?? true,
    dataSources
  };

  const resolveList = (nodes: Node[] | undefined) => (nodes ?? []).flatMap((n) => resolveNode(ctx, n, scope, schemaScope, "", "section"));

  const header: ResolvedRegion = { default: resolveList(template.header?.default) };
  if (template.page.header.differentFirstPage) header.firstPage = resolveList(template.header?.firstPage);
  const footer: ResolvedRegion = { default: resolveList(template.footer?.default) };
  if (template.page.footer.differentFirstPage) footer.firstPage = resolveList(template.footer?.firstPage);

  return { body: resolveList(template.body), header, footer, warnings, mode };
}

function isPageDependent(expr: string): boolean {
  try {
    return collectPaths(parseExpression(expr)).some((p) => p[0]?.kind === "prop" && p[0].name === "page");
  } catch {
    return false;
  }
}

function evalCtx(ctx: Ctx, scope: Scope, nodeId: string): EvalContext {
  return {
    scope,
    format: ctx.format,
    onWarning: (message, expr) => ctx.warnings.push({ message, nodeId, expr })
  };
}

function safeEvaluate(ctx: Ctx, scope: Scope, nodeId: string, expr: string): unknown {
  try {
    return evaluate(expr, evalCtx(ctx, scope, nodeId));
  } catch (e) {
    ctx.warnings.push({ message: `Invalid expression: ${(e as Error).message}`, nodeId, expr });
    return undefined;
  }
}

const labelFor = describeExpression;

function resolveSegments(ctx: Ctx, scope: Scope, schemaScope: SchemaScope, nodeId: string, value: string): { segments: ResolvedSegment[]; text: string; pageDependent: boolean } {
  const segments: ResolvedSegment[] = [];
  let pageDependent = false;
  for (const s of splitTemplate(value)) {
    if (s.kind === "text") {
      segments.push(s);
      continue;
    }
    const expr = s.expr!;
    const { label } = labelFor(expr, schemaScope);
    if (isPageDependent(expr)) {
      pageDependent = true;
      segments.push({ ...s, label, deferred: true });
      continue;
    }
    const v = safeEvaluate(ctx, scope, nodeId, expr);
    segments.push({ ...s, label, value: display(v) });
  }
  const text = segments.map((s) => (s.kind === "text" ? s.text : s.value ?? "")).join("");
  return { segments, text, pageDependent };
}

function resolveRichText(ctx: Ctx, scope: Scope, schemaScope: SchemaScope, nodeId: string, doc: RichTextDoc): { doc: RichTextDoc; pageDependent: boolean } {
  let pageDependent = false;
  const inline = (content: InlineNode[]): InlineNode[] =>
    content.map((n) => {
      if (n.type !== "token") {
        if (n.type === "text" && n.marks?.link?.includes("{{")) {
          const r = resolveSegments(ctx, scope, schemaScope, nodeId, n.marks.link);
          return { ...n, marks: { ...n.marks, link: r.text } };
        }
        return n;
      }
      const { label } = labelFor(n.expr, schemaScope);
      if (isPageDependent(n.expr)) {
        pageDependent = true;
        return { ...n, label, deferred: true };
      }
      return { ...n, label, value: display(safeEvaluate(ctx, scope, nodeId, n.expr)) };
    });
  const para = (p: Paragraph): Paragraph => ({ ...p, content: inline(p.content) });
  const out: RichTextDoc = {
    type: "doc",
    content: doc.content.map((b) => (b.type === "paragraph" ? para(b) : { ...b, content: b.content.map((li) => ({ ...li, content: li.content.map(para) })) }))
  };
  return { doc: out, pageDependent };
}

function getPath(obj: Record<string, unknown>, path: string): unknown {
  const parts = path.split(".");
  let cur: unknown = obj;
  for (const p of parts) {
    if (cur === null || cur === undefined || typeof cur !== "object") return undefined;
    cur = (cur as Record<string, unknown>)[p];
  }
  return cur;
}

function setPath(obj: Record<string, unknown>, path: string, value: unknown) {
  const parts = path.split(".");
  let cur: Record<string, unknown> = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const p = parts[i]!;
    if (typeof cur[p] !== "object" || cur[p] === null) cur[p] = {};
    cur = cur[p] as Record<string, unknown>;
  }
  cur[parts[parts.length - 1]!] = value;
}

function resolveFlow(def: BlockDefinition | undefined, node: Node, textStyleKeepWithNext: boolean | undefined): ResolvedFlow {
  const d: FlowOptions = def?.defaultFlow ?? {};
  const n: FlowOptions = node.flow ?? {};
  const isContainer = !!def?.container;
  const split = def?.split ?? (isContainer ? "children" : "none");
  return {
    breakBefore: n.breakBefore ?? d.breakBefore ?? "auto",
    breakAfter: n.breakAfter ?? d.breakAfter ?? "auto",
    keepTogether: n.keepTogether ?? d.keepTogether ?? false,
    keepWithNext: n.keepWithNext ?? d.keepWithNext ?? textStyleKeepWithNext ?? false,
    split,
    minOrphans: n.minOrphans ?? d.minOrphans ?? 2,
    minWidows: n.minWidows ?? d.minWidows ?? 2
  };
}

function roleOf(type: string): ResolvedNode["role"] {
  return type === "section" || type === "row" || type === "column" ? type : "block";
}

/**
 * Resolves a single template node into zero or more resolved nodes (zero when
 * hidden by a condition in preview mode).
 */
export function resolveNode(ctx: Ctx, node: Node, scope: Scope, schemaScope: SchemaScope, keyPrefix: string, _parentRole: ResolvedNode["role"]): ResolvedNode[] {
  const def = ctx.registry.get(node.type);
  const key = keyPrefix + node.id;
  const nodeId = node.id;

  // Visibility
  let hiddenByCondition = false;
  const cond = compileCondition(node.visibleWhen);
  if (cond) {
    const syntax = checkExpressionSyntax(cond);
    if (syntax) ctx.warnings.push({ message: `Invalid condition: ${syntax.message}`, nodeId, expr: cond });
    else {
      let visible = true;
      try {
        visible = evaluateCondition(cond, evalCtx(ctx, scope, nodeId));
      } catch (e) {
        ctx.warnings.push({ message: `Condition failed: ${(e as Error).message}`, nodeId, expr: cond });
      }
      if (!visible) {
        if (ctx.mode === "preview" || !ctx.designShowHidden) return [];
        hiddenByCondition = true;
      }
    }
  }

  if (!def) {
    ctx.warnings.push({ message: `Unknown block type "${node.type}"`, nodeId });
    return [
      {
        key,
        nodeId,
        type: node.type,
        role: roleOf(node.type),
        props: { ...node.props, __unknown: true },
        raw: node.props,
        style: resolveStyle({ theme: ctx.theme, styles: ctx.template.styles }),
        flow: resolveFlow(undefined, node, undefined),
        children: [],
        bindings: {},
        textSegments: {},
        richText: {},
        label: node.name ?? node.type,
        locked: node.locked
      }
    ];
  }

  const defaults = resolveDefaultProps(def);
  const raw: Record<string, unknown> = { ...defaults, ...node.props };
  const props: Record<string, unknown> = structuredClone(raw);
  const bindings: Record<string, BindingInfo> = {};
  const textSegments: Record<string, ResolvedSegment[]> = {};
  const richText: Record<string, RichTextDoc> = {};
  let pageDependent = false;

  const applySpec = (spec: BindingSpec, target: Record<string, unknown>, propPrefix: string) => {
    const propPath = propPrefix + spec.prop;
    const value = getPath(target, spec.prop);
    switch (spec.kind) {
      case "value": {
        if (typeof value !== "string" || value.trim() === "") return;
        const { label, type } = labelFor(value, schemaScope);
        bindings[propPath] = { prop: propPath, kind: "value", expr: value, label, type };
        if (isPageDependent(value)) {
          pageDependent = true;
          setPath(target, spec.prop, undefined);
          return;
        }
        setPath(target, spec.prop, safeEvaluate(ctx, scope, nodeId, value));
        return;
      }
      case "collection": {
        if (typeof value !== "string" || value.trim() === "") {
          setPath(target, spec.prop, []);
          return;
        }
        const { label, type } = labelFor(value, schemaScope);
        bindings[propPath] = { prop: propPath, kind: "collection", expr: value, label, type };
        const v = safeEvaluate(ctx, scope, nodeId, value);
        setPath(target, spec.prop, Array.isArray(v) ? v : v === undefined || v === null ? [] : [v]);
        return;
      }
      case "template": {
        if (typeof value !== "string") return;
        if (!value.includes("{{")) {
          textSegments[propPath] = [{ kind: "text", text: value }];
          return;
        }
        const r = resolveSegments(ctx, scope, schemaScope, nodeId, value);
        if (r.pageDependent) pageDependent = true;
        textSegments[propPath] = r.segments;
        const tokens = r.segments.filter((s) => s.kind === "token");
        if (tokens.length === 1 && r.segments.length === 1) {
          const { label, type } = labelFor(tokens[0]!.expr!, schemaScope);
          bindings[propPath] = { prop: propPath, kind: "template", expr: tokens[0]!.expr!, label, type };
        }
        setPath(target, spec.prop, r.text);
        return;
      }
      case "richtext": {
        if (!isRichTextDoc(value)) return;
        const r = resolveRichText(ctx, scope, schemaScope, nodeId, value);
        if (r.pageDependent) pageDependent = true;
        richText[propPath] = r.doc;
        return;
      }
      case "condition":
      case "list":
        return;
    }
  };

  for (const spec of bindingSpecsFor(def)) {
    if (spec.perItem) continue;
    if (spec.items) {
      const list = getPath(props, spec.prop);
      if (Array.isArray(list)) list.forEach((item, i) => item && typeof item === "object" && spec.items!.forEach((s) => !s.perItem && applySpec(s, item as Record<string, unknown>, `${spec.prop}.${i}.`)));
      continue;
    }
    applySpec(spec, props, "");
  }

  // Block-level resolve hook
  if (def.resolve) {
    const hookCtx: ResolveHookContext<Record<string, unknown>> = {
      props,
      node,
      scope,
      mode: ctx.mode,
      theme: ctx.theme,
      format: ctx.format,
      evaluate: (expr) => safeEvaluate(ctx, scope, nodeId, expr),
      evaluateIn: (expr, vars) => safeEvaluate(ctx, scope.child(vars), nodeId, expr),
      warn: (message) => ctx.warnings.push({ message, nodeId })
    };
    try {
      const out = def.resolve(hookCtx);
      if (out && typeof out === "object") Object.assign(props, out);
    } catch (e) {
      ctx.warnings.push({ message: `Block "${def.name}" failed to resolve: ${(e as Error).message}`, nodeId });
    }
  }

  // Style + flow
  const textStyleId = node.style?.textStyle ?? def.defaultTextStyle;
  const style = resolveStyle({
    theme: ctx.theme,
    styles: ctx.template.styles,
    textStyle: textStyleId,
    defaults: def.defaultStyle,
    overrides: node.style,
    allowOverrides: ctx.allowStyleOverrides
  });
  const textStyleDef = textStyleId ? ctx.template.styles.text[textStyleId] : undefined;
  const flow = resolveFlow(def, node, textStyleDef?.keepWithNext);

  // Children
  let children: ResolvedNode[] = [];
  let iterationInfo: ResolvedNode["iteration"] | undefined;
  let placeholder = false;
  const c = def.container;
  if (c?.repeat) {
    const listValue = props[c.repeat.collectionProp];
    const alias = (props[c.repeat.aliasProp ?? "alias"] as string) || "item";
    const collectionExpr = raw[c.repeat.collectionProp];
    let items = Array.isArray(listValue) ? listValue : [];
    const limit = c.repeat.limitProp ? Number(props[c.repeat.limitProp]) || undefined : undefined;
    if (limit) items = items.slice(0, limit);
    if (items.length > ctx.maxIterations) {
      ctx.warnings.push({ message: `Repeater truncated to ${ctx.maxIterations} items`, nodeId });
      items = items.slice(0, ctx.maxIterations);
    }
    let itemSchema: FieldSchema | undefined;
    if (typeof collectionExpr === "string" && collectionExpr.trim()) {
      try {
        let ast = parseExpression(collectionExpr);
        while (ast.kind === "pipe") ast = ast.input;
        const r = ast.kind === "path" ? resolvePathSchema(ast.segments, schemaScope) : {};
        itemSchema = r.schema?.type === "array" ? r.schema.itemSchema : undefined;
      } catch {
        itemSchema = undefined;
      }
    }
    if (ctx.mode === "design") {
      if (items.length === 0) {
        items = itemSchema ? [synthesizeValue(itemSchema, { now: ctx.format.now }, 0, 0)] : [{}];
        placeholder = true;
      }
      items = items.slice(0, ctx.designIterations);
    }
    const keepItem = c.repeat.keepItemTogetherProp ? !!props[c.repeat.keepItemTogetherProp] : false;
    const count = items.length;
    const childSchemaScope = schemaScope.child({
      [alias]: itemSchema ?? { type: "object", label: alias, fields: {} },
      index: { type: "number", label: "Index" },
      first: { type: "boolean", label: "Is first" },
      last: { type: "boolean", label: "Is last" },
      count: { type: "number", label: "Count" }
    });
    children = items.map((item, index) => {
      const iterScope = scope.child({ [alias]: item, item, index: index + 1, first: index === 0, last: index === count - 1, count });
      const iterKey = `${key}@${index}`;
      const iterChildren = (node.children ?? []).flatMap((ch) => resolveNode(ctx, ch, iterScope, childSchemaScope, `${iterKey}/`, "block"));
      const iteration: ResolvedNode = {
        key: iterKey,
        nodeId,
        type: "repeater-item",
        role: "block",
        props: { index, count },
        raw: {},
        style: resolveStyle({ theme: ctx.theme, styles: ctx.template.styles }),
        flow: { breakBefore: "auto", breakAfter: "auto", keepTogether: keepItem, keepWithNext: false, split: "children", minOrphans: 1, minWidows: 1 },
        children: iterChildren,
        bindings: {},
        textSegments: {},
        richText: {},
        iteration: { index, count, alias },
        label: `${alias} ${index + 1}`
      };
      return iteration;
    });
    iterationInfo = undefined;
    props.__itemCount = Array.isArray(listValue) ? listValue.length : 0;
  } else if (c?.conditional) {
    const condValue = compileCondition(raw[c.conditional.conditionProp] as never);
    let show = true;
    if (condValue) {
      const syntax = checkExpressionSyntax(condValue);
      if (syntax) ctx.warnings.push({ message: `Invalid condition: ${syntax.message}`, nodeId, expr: condValue });
      else {
        try {
          show = evaluateCondition(condValue, evalCtx(ctx, scope, nodeId));
        } catch (e) {
          ctx.warnings.push({ message: `Condition failed: ${(e as Error).message}`, nodeId, expr: condValue });
        }
      }
    }
    props.__conditionResult = show;
    if (show || ctx.mode === "design") {
      children = (node.children ?? []).flatMap((ch) => resolveNode(ctx, ch, scope, schemaScope, keyPrefix, "block"));
    }
  } else if (c) {
    children = (node.children ?? []).flatMap((ch) => resolveNode(ctx, ch, scope, schemaScope, keyPrefix, roleOf(node.type)));
  }

  const resolved: ResolvedNode = {
    key,
    nodeId,
    type: node.type,
    role: roleOf(node.type),
    props,
    raw,
    style,
    flow,
    children,
    bindings,
    textSegments,
    richText,
    label: node.name ?? def.canvas?.label?.(props) ?? def.name,
    locked: node.locked,
    pageDependent: pageDependent || undefined,
    placeholder: placeholder || undefined,
    iteration: iterationInfo
  };
  if (hiddenByCondition) resolved.props.__hiddenByCondition = true;
  if (node.type === "row") {
    const layout = Array.isArray(props.layout) ? (props.layout as number[]) : children.map(() => 12 / Math.max(1, children.length));
    resolved.layout = { columns: layout };
    children.forEach((col, i) => {
      col.span = layout[i] ?? 12 / Math.max(1, children.length);
    });
  }
  return [resolved];
}

/** Convenience for tests and tooling: resolves and returns display text of an expression. */
export function evaluateToText(expr: string, data: ReportData, format?: Partial<FormatContext>): string {
  const scope = new Scope({ now: format?.now ?? new Date() }).child(data);
  return toDisplayString(
    evaluate(expr, {
      scope,
      format: { locale: "en-GB", currency: "GBP", ...format }
    })
  );
}
